import { createContext, useContext, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  isPublicationOpen,
  withExpectations,
  type Org,
  type ReelDetail,
  type Script,
  type ScriptOp,
  type UploadResponse,
} from '@reelmachine/core';
import { postToPresignedUrl, type ApiClient, type BodyOf, type ChatProposal } from './client';

export const ApiContext = createContext<ApiClient | null>(null);

export function useApi(): ApiClient {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi needs an ApiContext provider');
  return api;
}

/** Poll every 2 s while a job runs. */
export const POLL_MS = 2000;

export const keys = {
  me: ['me'] as const,
  org: (orgId: string) => ['org', orgId] as const,
  reels: (orgId: string) => ['reels', orgId] as const,
  reel: (reelId: string) => ['reel', reelId] as const,
  chat: (reelId: string) => ['chat', reelId] as const,
  events: (reelId: string) => ['events', reelId] as const,
  /** Client-only: the open chat proposal of a reel (survives switching tabs). */
  proposal: (reelId: string) => ['chatProposal', reelId] as const,
};

/** Accepts `{ org }` or a bare Org, so the app keeps working if the API returns either. */
function unwrapOrg(res: { org: Org } | Org): Org {
  return 'org' in res && typeof res.org === 'object' ? res.org : (res as Org);
}

export function setReelDetail(qc: QueryClient, detail: ReelDetail): void {
  qc.setQueryData(keys.reel(detail.reel.id), detail);
  void qc.invalidateQueries({ queryKey: ['reels', detail.reel.orgId] });
}

export function setScript(qc: QueryClient, reelId: string, script: Script): void {
  qc.setQueryData<ReelDetail>(keys.reel(reelId), (d) => (d ? { ...d, script } : d));
}

// ---- queries ----

export function useMe() {
  const api = useApi();
  return useQuery({ queryKey: keys.me, queryFn: () => api.call('me', {}), staleTime: 60_000 });
}

export function useOrg(orgId: string | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.org(orgId ?? ''),
    queryFn: async () => unwrapOrg(await api.call('getOrg', { orgId: orgId! })),
    enabled: !!orgId,
    initialData: () => qc.getQueryData<{ orgs: Org[] }>(keys.me)?.orgs.find((o) => o.id === orgId),
    initialDataUpdatedAt: 0,
    refetchInterval: (q) => (q.state.data?.job?.status === 'running' ? POLL_MS : false),
  });
}

// ---- selected org (people who work for several orgs, e.g. an editor for three clients) ----
const ORG_KEY = 'rm.org';
const orgListeners = new Set<() => void>();
function readSelectedOrg(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}
/** Remembers the org to work in (per browser) and switches all screens to it. */
export function selectOrg(orgId: string) {
  try {
    localStorage.setItem(ORG_KEY, orgId);
  } catch {
    // private mode: the choice lasts until reload
  }
  memorySelection = orgId;
  orgListeners.forEach((l) => l());
}
let memorySelection: string | null = null;
function useSelectedOrg(): string | null {
  return useSyncExternalStore(
    (l) => {
      orgListeners.add(l);
      return () => orgListeners.delete(l);
    },
    () => memorySelection ?? readSelectedOrg(),
    () => null,
  );
}

/** The org the person works in: the one they picked, if they are a member, else their first membership. */
export function useCurrentOrg() {
  const me = useMe();
  const selected = useSelectedOrg();
  const memberOf = me.data?.memberships.map((m) => m.orgId) ?? [];
  const orgId =
    (selected && memberOf.includes(selected) ? selected : undefined) ??
    me.data?.memberships[0]?.orgId ??
    me.data?.orgs[0]?.id;
  const org = useOrg(orgId);
  return { me, orgId, org: org.data, query: org };
}

export function useReels(orgId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: keys.reels(orgId ?? ''),
    queryFn: async () => (await api.call('listReels', { orgId: orgId! })).reels,
    enabled: !!orgId,
    refetchInterval: (q) => (q.state.data?.some((r) => r.job?.status === 'running') ? POLL_MS : false),
  });
}

/** Whether a reel is waiting for something the server does on its own: a job, the video checks, a publication. */
export function isReelBusy(d: ReelDetail | undefined): boolean {
  if (!d) return false;
  const { reel } = d;
  return (
    reel.job?.status === 'running' || reel.render?.quality?.status === 'pending' || isPublicationOpen(reel.publication)
  );
}

export function useReel(reelId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.reel(reelId),
    queryFn: () => api.call('getReel', { reelId }),
    refetchInterval: (q) => (isReelBusy(q.state.data) ? POLL_MS : false),
  });
}

export function useChatMessages(reelId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.chat(reelId),
    queryFn: async () => (await api.call('listChat', { reelId })).messages,
  });
}

export function useEvents(reelId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.events(reelId),
    queryFn: async () => (await api.call('events', { reelId })).events,
  });
}

// ---- mutations ----

export function useUpdateOrg(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BodyOf<'updateOrg'>) => unwrapOrg(await api.call('updateOrg', { orgId }, body)),
    onSuccess: (org) => qc.setQueryData(keys.org(orgId), org),
  });
}

export function useReadChannel(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrapOrg(await api.call('readChannel', { orgId })),
    onSuccess: (org) => qc.setQueryData(keys.org(orgId), org),
  });
}

export function useConnectZernio(orgId: string) {
  const api = useApi();
  return useMutation({ mutationFn: () => api.call('connectZernio', { orgId }) });
}

export function useCreateReel(orgId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BodyOf<'createReel'>) => api.call('createReel', { orgId }, body),
    onSuccess: (d) => setReelDetail(qc, d),
  });
}

/** Asks for a presigned URL and uploads the file. Returns the S3 key. */
export async function uploadFile(
  api: ApiClient,
  orgId: string,
  body: BodyOf<'upload'>,
  file: Blob,
): Promise<UploadResponse> {
  const up = await api.call('upload', { orgId }, body);
  await postToPresignedUrl(up, file);
  return up;
}

type ReelAction = 'approve' | 'render' | 'writePost' | 'publish' | 'regenerate' | 'recheckQuality' | 'cancelJob';

/** Body-less reel actions that return a ReelDetail. */
export function useReelAction(reelId: string, action: ReelAction) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // Publish carries a fresh request id per click: a retried or doubled request returns the same intent (R01).
    mutationFn: () =>
      action === 'publish'
        ? api.call('publish', { reelId }, { requestId: `req_${crypto.randomUUID()}` })
        : api.call(action, { reelId }),
    onSuccess: (d) => {
      setReelDetail(qc, d);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
  });
}

export function useSelectTake(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: number) => api.call('selectTake', { reelId }, { n }),
    onSuccess: (d) => setReelDetail(qc, d),
  });
}

export function useAddTake(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BodyOf<'addTake'>) => api.call('addTake', { reelId }, body),
    onSuccess: (d) => setReelDetail(qc, d),
  });
}

/** Staff only: accept reviewable video warnings with a reason. */
export function useOverrideQuality(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.call('overrideQuality', { reelId }, { reason }),
    onSuccess: (d) => {
      setReelDetail(qc, d);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
  });
}

/** Staff only: settle a publication stuck in "unknown" after checking Instagram by hand. */
export function useResolvePublication(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BodyOf<'resolvePublication'>) => api.call('resolvePublication', { reelId }, body),
    onSuccess: (d) => {
      setReelDetail(qc, d);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
  });
}

export function useSetCaption(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caption: string) => api.call('setCaption', { reelId }, { caption }),
    onSuccess: (d) => qc.setQueryData(keys.reel(reelId), d),
  });
}

export function useSendChat(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => api.call('chat', { reelId }, { message }),
    onSuccess: (res) => {
      setScript(qc, reelId, res.script);
      qc.setQueryData(keys.proposal(reelId), res.proposal ?? null);
      qc.setQueryData(keys.chat(reelId), (old: unknown) => [
        ...((old as unknown[] | undefined) ?? []),
        ...res.messages,
      ]);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
    // The API may have stored the user message (or changed the script) before failing: reload both.
    onError: () => {
      void qc.invalidateQueries({ queryKey: keys.chat(reelId) });
      void qc.invalidateQueries({ queryKey: keys.reel(reelId) });
    },
  });
}

export function useUndoChat(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.call('undo', { reelId }, { messageId }),
    onSuccess: (res) => {
      setScript(qc, reelId, res.script);
      void qc.invalidateQueries({ queryKey: keys.chat(reelId) });
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: keys.chat(reelId) });
      void qc.invalidateQueries({ queryKey: keys.reel(reelId) });
    },
  });
}

/** The assistant's proposal for fields a person changed while it was thinking, until applied or dismissed. */
export function useChatProposal(reelId: string) {
  const qc = useQueryClient();
  const q = useQuery<ChatProposal | null>({
    queryKey: keys.proposal(reelId),
    queryFn: () => null,
    initialData: null,
    staleTime: Infinity,
  });
  const dismiss = () => qc.setQueryData(keys.proposal(reelId), null);
  return { proposal: q.data, dismiss };
}

/** Clears the `expected` values from the model's base, then expects the values the person sees now. */
export function proposalOps(script: Script, ops: readonly ScriptOp[]): ScriptOp[] {
  return withExpectations(
    script,
    ops.map((op) => ('expected' in op ? { ...op, expected: undefined } : op)),
  );
}

/** Applies a chat proposal on top of the current script, as an explicit edit by the person. */
export function useApplyProposal(reelId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ script, ops }: { script: Script; ops: readonly ScriptOp[] }) =>
      api.call('patchScript', { reelId }, { baseVersion: script.version, ops: proposalOps(script, ops) }),
    onSuccess: (res) => {
      setScript(qc, reelId, res.script);
      qc.setQueryData(keys.proposal(reelId), null);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    },
    onError: () => void qc.invalidateQueries({ queryKey: keys.reel(reelId) }),
  });
}
